/**
 * Sudoku Engine — Core Game Logic
 *
 * Handles puzzle generation, validation, move placement, hints, and undo.
 *
 * Puzzle generation algorithm:
 * 1. Create a fully solved board using backtracking
 *    - Fill diagonal 3×3 boxes first (they don't conflict with each other)
 *    - Solve the rest with backtracking
 * 2. Remove cells one at a time based on difficulty
 *    - After each removal, verify the puzzle still has a unique solution
 */

import {
  SudokuBoard,
  SudokuCell,
  SudokuGameState,
  SudokuDifficulty,
  SudokuPosition,
  SudokuMove,
  BOARD_SIZE,
  BOX_SIZE,
  DIFFICULTY_CLUES,
} from './types';

export class SudokuEngine {
  private gameState: SudokuGameState | null = null;

  // =========================================================================
  // Public API
  // =========================================================================

  /** Generate a new puzzle and initialize game state. */
  initializeGame(difficulty: SudokuDifficulty): SudokuGameState {
    const solution = this.generateSolvedBoard();
    const board = this.createPuzzle(solution, difficulty);

    this.gameState = {
      board,
      solution,
      difficulty,
      selectedCell: null,
      timer: 0,
      moveHistory: [],
      hintsUsed: 0,
      gameStatus: 'playing',
      notesMode: false,
      gameId: this.generateId(),
      startedAt: Date.now(),
    };

    return this.getGameState();
  }

  /** Get a deep copy of the current game state. */
  getGameState(): SudokuGameState {
    if (!this.gameState) {
      throw new Error('Game not initialized');
    }
    return JSON.parse(JSON.stringify(this.gameState));
  }

  /** Place a number in a cell. Returns updated game state. */
  placeNumber(row: number, col: number, value: number): SudokuGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'playing') throw new Error('Game is not in progress');

    const cell = this.gameState.board[row][col];
    if (cell.isGiven) throw new Error('Cannot modify a given cell');
    if (value < 1 || value > 9) throw new Error('Value must be between 1 and 9');

    const move: SudokuMove = {
      position: { row, col },
      previousValue: cell.value,
      newValue: value,
      previousNotes: [...cell.notes],
      newNotes: [],
      type: 'place',
      timestamp: Date.now(),
    };

    cell.value = value;
    cell.notes = [];
    this.gameState.moveHistory.push(move);

    this.validateBoard();

    if (this.isComplete()) {
      this.gameState.gameStatus = 'completed';
    }

    return this.getGameState();
  }

  /** Remove a number from a cell. */
  removeNumber(row: number, col: number): SudokuGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'playing') throw new Error('Game is not in progress');

    const cell = this.gameState.board[row][col];
    if (cell.isGiven) throw new Error('Cannot modify a given cell');
    if (cell.value === null) return this.getGameState();

    const move: SudokuMove = {
      position: { row, col },
      previousValue: cell.value,
      newValue: null,
      previousNotes: [...cell.notes],
      newNotes: [...cell.notes],
      type: 'remove',
      timestamp: Date.now(),
    };

    cell.value = null;
    this.gameState.moveHistory.push(move);

    this.validateBoard();

    return this.getGameState();
  }

  /** Toggle a pencil note on a cell. */
  toggleNote(row: number, col: number, value: number): SudokuGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'playing') throw new Error('Game is not in progress');

    const cell = this.gameState.board[row][col];
    if (cell.isGiven) throw new Error('Cannot modify a given cell');
    if (cell.value !== null) throw new Error('Clear the cell before adding notes');
    if (value < 1 || value > 9) throw new Error('Value must be between 1 and 9');

    const previousNotes = [...cell.notes];
    const noteIndex = cell.notes.indexOf(value);

    if (noteIndex >= 0) {
      cell.notes.splice(noteIndex, 1);
    } else {
      cell.notes.push(value);
      cell.notes.sort();
    }

    const move: SudokuMove = {
      position: { row, col },
      previousValue: null,
      newValue: null,
      previousNotes,
      newNotes: [...cell.notes],
      type: 'note',
      timestamp: Date.now(),
    };

    this.gameState.moveHistory.push(move);

    return this.getGameState();
  }

  /** Undo the last move. */
  undo(): SudokuGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'playing') throw new Error('Game is not in progress');
    if (this.gameState.moveHistory.length === 0) return this.getGameState();

    const lastMove = this.gameState.moveHistory.pop()!;
    const cell = this.gameState.board[lastMove.position.row][lastMove.position.col];

    cell.value = lastMove.previousValue;
    cell.notes = [...lastMove.previousNotes];

    this.validateBoard();

    return this.getGameState();
  }

  /** Reveal one correct cell as a hint. Returns the position revealed, or null if no hints available. */
  getHint(): { state: SudokuGameState; position: SudokuPosition | null } {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'playing') throw new Error('Game is not in progress');

    // Find all empty or incorrect cells
    const candidates: SudokuPosition[] = [];
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        const cell = this.gameState.board[row][col];
        if (!cell.isGiven && cell.value !== this.gameState.solution[row][col]) {
          candidates.push({ row, col });
        }
      }
    }

    if (candidates.length === 0) {
      return { state: this.getGameState(), position: null };
    }

    // Pick a random candidate
    const pos = candidates[Math.floor(Math.random() * candidates.length)];
    const cell = this.gameState.board[pos.row][pos.col];
    const correctValue = this.gameState.solution[pos.row][pos.col];

    const move: SudokuMove = {
      position: pos,
      previousValue: cell.value,
      newValue: correctValue,
      previousNotes: [...cell.notes],
      newNotes: [],
      type: 'place',
      timestamp: Date.now(),
    };

    cell.value = correctValue;
    cell.notes = [];
    this.gameState.moveHistory.push(move);
    this.gameState.hintsUsed++;

    this.validateBoard();

    if (this.isComplete()) {
      this.gameState.gameStatus = 'completed';
    }

    return { state: this.getGameState(), position: pos };
  }

  /** Update the timer value. */
  updateTimer(seconds: number): void {
    if (this.gameState) {
      this.gameState.timer = seconds;
    }
  }

  /** Set selected cell. */
  setSelectedCell(position: SudokuPosition | null): void {
    if (this.gameState) {
      this.gameState.selectedCell = position;
    }
  }

  /** Toggle notes mode. */
  toggleNotesMode(): boolean {
    if (this.gameState) {
      this.gameState.notesMode = !this.gameState.notesMode;
      return this.gameState.notesMode;
    }
    return false;
  }

  /** Check if the board is completely and correctly filled. */
  isComplete(): boolean {
    if (!this.gameState) return false;

    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        const cell = this.gameState.board[row][col];
        if (cell.value !== this.gameState.solution[row][col]) {
          return false;
        }
      }
    }
    return true;
  }

  /** Validate the board and mark error cells. */
  validateBoard(): void {
    if (!this.gameState) return;

    // Clear all errors first
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        this.gameState.board[row][col].isError = false;
      }
    }

    // Check rows
    for (let row = 0; row < BOARD_SIZE; row++) {
      this.markDuplicates(
        this.getCellsInRow(row)
      );
    }

    // Check columns
    for (let col = 0; col < BOARD_SIZE; col++) {
      this.markDuplicates(
        this.getCellsInCol(col)
      );
    }

    // Check 3×3 boxes
    for (let boxRow = 0; boxRow < BOX_SIZE; boxRow++) {
      for (let boxCol = 0; boxCol < BOX_SIZE; boxCol++) {
        this.markDuplicates(
          this.getCellsInBox(boxRow * BOX_SIZE, boxCol * BOX_SIZE)
        );
      }
    }
  }

  /** Serialize game state for persistence. */
  serialize(): string {
    return JSON.stringify(this.gameState);
  }

  /** Deserialize and restore game state. */
  deserialize(data: string): SudokuGameState {
    this.gameState = JSON.parse(data);
    return this.getGameState();
  }

  // =========================================================================
  // Puzzle Generation
  // =========================================================================

  /** Generate a fully solved 9×9 board. */
  private generateSolvedBoard(): number[][] {
    const board: number[][] = Array.from({ length: BOARD_SIZE }, () =>
      Array(BOARD_SIZE).fill(0)
    );

    // Fill diagonal 3×3 boxes first (they don't conflict with each other)
    for (let i = 0; i < BOARD_SIZE; i += BOX_SIZE) {
      this.fillBox(board, i, i);
    }

    // Solve the rest with backtracking
    this.solveSudoku(board);

    return board;
  }

  /** Fill a 3×3 box with random digits 1-9. */
  private fillBox(board: number[][], startRow: number, startCol: number): void {
    const nums = this.shuffleArray([1, 2, 3, 4, 5, 6, 7, 8, 9]);
    let idx = 0;
    for (let r = 0; r < BOX_SIZE; r++) {
      for (let c = 0; c < BOX_SIZE; c++) {
        board[startRow + r][startCol + c] = nums[idx++];
      }
    }
  }

  /** Solve a Sudoku board using backtracking. Returns true if solved. */
  private solveSudoku(board: number[][]): boolean {
    const empty = this.findEmpty(board);
    if (!empty) return true; // No empty cells — solved

    const [row, col] = empty;
    const nums = this.shuffleArray([1, 2, 3, 4, 5, 6, 7, 8, 9]);

    for (const num of nums) {
      if (this.isValidPlacement(board, row, col, num)) {
        board[row][col] = num;
        if (this.solveSudoku(board)) return true;
        board[row][col] = 0;
      }
    }

    return false;
  }

  /** Find the first empty cell (value 0). */
  private findEmpty(board: number[][]): [number, number] | null {
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if (board[row][col] === 0) return [row, col];
      }
    }
    return null;
  }

  /** Check if placing num at (row, col) is valid. */
  private isValidPlacement(
    board: number[][],
    row: number,
    col: number,
    num: number
  ): boolean {
    // Check row
    for (let c = 0; c < BOARD_SIZE; c++) {
      if (board[row][c] === num) return false;
    }

    // Check column
    for (let r = 0; r < BOARD_SIZE; r++) {
      if (board[r][col] === num) return false;
    }

    // Check 3×3 box
    const boxStartRow = Math.floor(row / BOX_SIZE) * BOX_SIZE;
    const boxStartCol = Math.floor(col / BOX_SIZE) * BOX_SIZE;
    for (let r = boxStartRow; r < boxStartRow + BOX_SIZE; r++) {
      for (let c = boxStartCol; c < boxStartCol + BOX_SIZE; c++) {
        if (board[r][c] === num) return false;
      }
    }

    return true;
  }

  /** Create a puzzle by removing cells from a solved board. */
  private createPuzzle(solution: number[][], difficulty: SudokuDifficulty): SudokuBoard {
    const { min, max } = DIFFICULTY_CLUES[difficulty];
    const targetClues = min + Math.floor(Math.random() * (max - min + 1));
    const totalCells = BOARD_SIZE * BOARD_SIZE;
    const cellsToRemove = totalCells - targetClues;

    // Start with a full board
    const puzzleValues: number[][] = solution.map((row) => [...row]);

    // Create a list of all positions and shuffle them
    const positions: [number, number][] = [];
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        positions.push([r, c]);
      }
    }
    this.shuffleArrayInPlace(positions);

    let removed = 0;
    for (const [row, col] of positions) {
      if (removed >= cellsToRemove) break;

      const backup = puzzleValues[row][col];
      puzzleValues[row][col] = 0;

      // Check if the puzzle still has a unique solution
      if (this.countSolutions(puzzleValues, 2) === 1) {
        removed++;
      } else {
        // Restore — removing this cell creates multiple solutions
        puzzleValues[row][col] = backup;
      }
    }

    // Convert to SudokuBoard
    const board: SudokuBoard = [];
    for (let row = 0; row < BOARD_SIZE; row++) {
      const boardRow: SudokuCell[] = [];
      for (let col = 0; col < BOARD_SIZE; col++) {
        const val = puzzleValues[row][col];
        boardRow.push({
          value: val === 0 ? null : val,
          isGiven: val !== 0,
          notes: [],
          isError: false,
        });
      }
      board.push(boardRow);
    }

    return board;
  }

  /**
   * Count solutions for a puzzle (up to maxCount).
   * Used to verify unique solution during puzzle generation.
   */
  private countSolutions(board: number[][], maxCount: number): number {
    const copy = board.map((row) => [...row]);
    let count = 0;

    const solve = (): boolean => {
      if (count >= maxCount) return true; // Early exit

      const empty = this.findEmpty(copy);
      if (!empty) {
        count++;
        return count >= maxCount;
      }

      const [row, col] = empty;
      for (let num = 1; num <= 9; num++) {
        if (this.isValidPlacement(copy, row, col, num)) {
          copy[row][col] = num;
          if (solve()) {
            copy[row][col] = 0;
            return true;
          }
          copy[row][col] = 0;
        }
      }

      return false;
    };

    solve();
    return count;
  }

  // =========================================================================
  // Validation Helpers
  // =========================================================================

  private getCellsInRow(row: number): { cell: SudokuCell; row: number; col: number }[] {
    return this.gameState!.board[row].map((cell, col) => ({ cell, row, col }));
  }

  private getCellsInCol(col: number): { cell: SudokuCell; row: number; col: number }[] {
    return this.gameState!.board.map((boardRow, row) => ({
      cell: boardRow[col],
      row,
      col,
    }));
  }

  private getCellsInBox(
    startRow: number,
    startCol: number
  ): { cell: SudokuCell; row: number; col: number }[] {
    const cells: { cell: SudokuCell; row: number; col: number }[] = [];
    for (let r = startRow; r < startRow + BOX_SIZE; r++) {
      for (let c = startCol; c < startCol + BOX_SIZE; c++) {
        cells.push({ cell: this.gameState!.board[r][c], row: r, col: c });
      }
    }
    return cells;
  }

  /** Mark duplicate values in a group of cells. */
  private markDuplicates(
    cells: { cell: SudokuCell; row: number; col: number }[]
  ): void {
    const valueCounts = new Map<number, { cell: SudokuCell; row: number; col: number }[]>();

    for (const entry of cells) {
      if (entry.cell.value !== null) {
        const existing = valueCounts.get(entry.cell.value) || [];
        existing.push(entry);
        valueCounts.set(entry.cell.value, existing);
      }
    }

    for (const [, entries] of valueCounts) {
      if (entries.length > 1) {
        for (const entry of entries) {
          entry.cell.isError = true;
        }
      }
    }
  }

  // =========================================================================
  // Utility
  // =========================================================================

  private shuffleArray<T>(array: T[]): T[] {
    const copy = [...array];
    this.shuffleArrayInPlace(copy);
    return copy;
  }

  private shuffleArrayInPlace<T>(array: T[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }

  private generateId(): string {
    return `sudoku_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  }
}
